import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMigration, getMigrationSites, getLicenses, getCertificates, getE911, getRooms, getDrTests, getRunbookScoreboard } from "../lib/api";
import { StepContent } from "../components/StepContent";
import { Cloud, Printer, ArrowLeft, DatabaseBackup, RotateCcw, Shield, MapPin, KeyRound, ShieldAlert, Siren, MonitorPlay, LifeBuoy, Route } from "lucide-react";
import { BrandLetterhead } from "../components/BrandLetterhead";

const SITE_LABEL = { pending: "Pending", onboarded: "Onboarded", cutover: "Cutover" };
const LIC_LABEL = { active: "Active", expiring: "Expiring", expired: "Expired", needed: "Needed" };
const E911_LABEL = { compliant: "Compliant", review: "Needs Review", pending: "Pending", "non-compliant": "Non-Compliant" };
const ROOM_LABEL = { registered: "Registered", offline: "Offline", issue: "Issue", pending: "Pending" };
const DR_LABEL = { pass: "Pass", partial: "Partial", fail: "Fail" };

// Days until a YYYY-MM-DD date (null if unparseable). Negative = past.
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d - new Date().setHours(0, 0, 0, 0)) / 86400000);
}
function certStatusLabel(dateStr) {
  const d = daysUntil(dateStr);
  if (d === null) return { label: "No date", tone: "muted" };
  if (d < 0) return { label: "Expired", tone: "red" };
  if (d <= 30) return { label: `${d}d left`, tone: "red" };
  if (d <= 90) return { label: `${d}d left`, tone: "amber" };
  return { label: "Valid", tone: "ok" };
}

const Section = ({ title, icon: Icon, children }) => (
  <section className="mt-8">
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold mb-3 border-b border-zinc-800 pb-2">
      {Icon && <Icon className="h-4 w-4" />} {title}
    </div>
    {children}
  </section>
);

const StepCard = ({ step, idx }) => (
  <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4 mb-3">
    <div className="font-heading font-bold text-white text-sm">{idx != null ? `${idx}. ` : ""}{step.title}</div>
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">{step.layer}</div>
    <StepContent step={step} />
  </div>
);

export default function MigrationExport() {
  const [data, setData] = useState(null);
  const [sites, setSites] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [certs, setCerts] = useState([]);
  const [e911, setE911] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [drTests, setDrTests] = useState([]);
  const [scoreboard, setScoreboard] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    getMigration().then(setData).catch(() => {});
    getMigrationSites().then(setSites).catch(() => {});
    getLicenses().then(setLicenses).catch(() => {});
    getCertificates().then(setCerts).catch(() => {});
    getE911().then(setE911).catch(() => {});
    getRooms().then(setRooms).catch(() => {});
    getDrTests().then(setDrTests).catch(() => {});
    getRunbookScoreboard().then(setScoreboard).catch(() => {});
  }, []);

  if (!data) return <div className="min-h-screen bg-background text-zinc-500 font-mono flex items-center justify-center">Loading playbook…</div>;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="print:hidden border-b border-zinc-800 bg-zinc-950 flex items-center justify-between px-6 py-3" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
        <button onClick={() => navigate("/migration")} data-testid="export-back-btn" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Migration
        </button>
        <button onClick={() => window.print()} data-testid="export-print-btn"
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors">
          <Printer className="h-4 w-4" /> Export / Print PDF
        </button>
      </div>

      <div className="max-w-3xl mx-auto p-6 sm:p-8">
        <BrandLetterhead title={data.title?.replace(/^UC Engineer Pro[ —-]*/i, "") || "Migration Audit"} subtitle="Full Migration & Estate Audit" />

        <p className="text-zinc-400 mt-4 leading-relaxed">{data.summary}</p>
        <div className="flex flex-wrap gap-2 mt-4 text-[12px] text-zinc-300">
          <span className="font-mono px-2 py-1 rounded border border-zinc-700 bg-zinc-950">CUCM {data.scope.cucm_version}</span>
          <span className="font-mono px-2 py-1 rounded border border-zinc-700 bg-zinc-950">{data.scope.sites} sites</span>
          <span className="font-mono px-2 py-1 rounded border border-zinc-700 bg-zinc-950">{data.scope.pstn_strategy}</span>
        </div>

        <Section title="Target Architecture">
          <pre className="rounded-md border border-zinc-800 bg-black p-4 overflow-x-auto"><code className="font-mono text-[11px] leading-relaxed text-emerald-300 whitespace-pre">{data.architecture}</code></pre>
        </Section>

        <Section title="Per-Site Runbook" icon={MapPin}>
          <div className="space-y-2">
            {sites.map((s) => (
              <div key={s.id} className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white font-semibold">{s.name}</span>
                  <span className="text-[11px] font-semibold text-zinc-300">{SITE_LABEL[s.status] || s.status}</span>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1 text-[12px] text-zinc-400">
                  <span><span className="text-zinc-600">Carrier:</span> {s.carrier || "—"}</span>
                  <span><span className="text-zinc-600">Circuits:</span> <span className="font-mono">{s.circuit_ids || "—"}</span></span>
                  <span><span className="text-zinc-600">FOC:</span> {s.foc_date || "—"}</span>
                </div>
                {s.notes && <p className="text-[12px] text-zinc-500 mt-1">{s.notes}</p>}
                {s.components && Object.values(s.components).some(Boolean) && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {Object.entries(s.components).filter(([, v]) => v).map(([k]) => (
                      <span key={k} className="text-[11px] px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 capitalize">{k} ✓</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>

        {scoreboard && scoreboard.sites.some((s) => s.gates_approved > 0 || s.runbook_pct > 0) && (
          <Section title="Migration Runbook Status" icon={Route}>
            <div className="rounded-md border border-zinc-800 overflow-hidden">
              <div className="grid grid-cols-[1.6fr_0.7fr_0.7fr_2.4fr] gap-2 px-3 py-2 bg-zinc-950 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                <span>Location</span><span>Runbook</span><span>Gates</span><span>Phase gate sign-off</span>
              </div>
              {scoreboard.sites.map((s) => (
                <div key={s.site_id} className="grid grid-cols-[1.6fr_0.7fr_0.7fr_2.4fr] gap-2 px-3 py-2 border-b border-zinc-900 text-[12px] items-start">
                  <div>
                    <div className="text-zinc-200">{s.name}</div>
                    <div className="text-[10px] text-zinc-500">{s.current_phase}</div>
                  </div>
                  <span className="font-mono text-zinc-300">{s.runbook_pct}%</span>
                  <span className="font-mono text-zinc-300">{s.gates_approved}/{s.gates_total}</span>
                  <div className="flex flex-col gap-0.5">
                    {s.phases.map((p) => (
                      <span key={p.id} className="text-[11px] text-zinc-400">
                        <span className={p.approved ? "text-emerald-400" : "text-zinc-600"}>{p.approved ? "✓" : "○"}</span>{" "}
                        {p.name} — {p.pct}%
                        {p.approved && (p.approver || p.approved_at) ? (
                          <span className="text-zinc-500"> · {p.approver || "—"}{p.approved_at ? ` (${new Date(p.approved_at).toLocaleDateString()})` : ""}</span>
                        ) : null}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

          <Section title="License Summary" icon={KeyRound}>
            <div className="rounded-md border border-zinc-800 overflow-hidden">
              <div className="grid grid-cols-[1.8fr_0.9fr_0.6fr_0.6fr_0.8fr_0.9fr] gap-2 px-3 py-2 bg-zinc-950 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                <span>License</span><span>Category</span><span>Entitled</span><span>Assigned</span><span>Status</span><span>Renewal</span>
              </div>
              {licenses.map((l) => {
                const over = (l.assigned || 0) > (l.entitled || 0);
                return (
                  <div key={l.id} className="grid grid-cols-[1.8fr_0.9fr_0.6fr_0.6fr_0.8fr_0.9fr] gap-2 px-3 py-2 border-b border-zinc-900 last:border-0 items-center">
                    <div>
                      <div className="text-[13px] text-white">{l.name}</div>
                      <div className="text-[10px] text-zinc-500">{l.vendor}</div>
                    </div>
                    <div className="text-[12px] text-zinc-400">{l.category}</div>
                    <div className="text-[12px] font-mono text-zinc-300">{l.entitled}</div>
                    <div className={`text-[12px] font-mono ${over ? "text-red-400" : "text-zinc-300"}`}>{l.assigned}</div>
                    <div className="text-[11px] text-zinc-300">{LIC_LABEL[l.status] || l.status}</div>
                    <div className="text-[12px] font-mono text-zinc-400">{l.renewal_date || "—"}</div>
                  </div>
                );
              })}
              <div className="grid grid-cols-[1.8fr_0.9fr_0.6fr_0.6fr_0.8fr_0.9fr] gap-2 px-3 py-2 bg-zinc-950 text-[12px] font-semibold">
                <span className="text-white">Totals</span><span></span>
                <span className="font-mono text-zinc-200">{licenses.reduce((a, l) => a + (l.entitled || 0), 0)}</span>
                <span className="font-mono text-zinc-200">{licenses.reduce((a, l) => a + (l.assigned || 0), 0)}</span>
                <span></span><span></span>
              </div>
            </div>
          </Section>
        )}

        {certs.length > 0 && (
          <Section title="Certificate Expiry" icon={ShieldAlert}>
            <div className="rounded-md border border-zinc-800 overflow-hidden">
              <div className="grid grid-cols-[1.8fr_1fr_1.4fr_1fr_0.9fr] gap-2 px-3 py-2 bg-zinc-950 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                <span>Certificate</span><span>Type</span><span>Node / CN</span><span>Expires</span><span>Status</span>
              </div>
              {certs.map((c) => {
                const st = certStatusLabel(c.expires);
                const cls = st.tone === "red" ? "text-red-400" : st.tone === "amber" ? "text-amber-400" : "text-zinc-300";
                return (
                  <div key={c.id} className="grid grid-cols-[1.8fr_1fr_1.4fr_1fr_0.9fr] gap-2 px-3 py-2 border-b border-zinc-900 last:border-0 items-center">
                    <div className="text-[13px] text-white">{c.name}</div>
                    <div className="text-[12px] text-zinc-400">{c.cert_type}</div>
                    <div className="text-[12px] font-mono text-zinc-400 truncate">{c.issued_to || c.node || "—"}</div>
                    <div className="text-[12px] font-mono text-zinc-400">{c.expires || "—"}</div>
                    <div className={`text-[12px] ${cls}`}>{st.label}</div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {e911.length > 0 && (
          <Section title="E911 Compliance" icon={Siren}>
            <div className="rounded-md border border-zinc-800 overflow-hidden">
              <div className="grid grid-cols-[1fr_0.9fr_0.9fr_1.8fr_0.9fr] gap-2 px-3 py-2 bg-zinc-950 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                <span>Site</span><span>ERL</span><span>ELIN</span><span>Dispatchable Location</span><span>Status</span>
              </div>
              {e911.map((r) => (
                <div key={r.id} className="grid grid-cols-[1fr_0.9fr_0.9fr_1.8fr_0.9fr] gap-2 px-3 py-2 border-b border-zinc-900 last:border-0 items-center">
                  <div className="text-[13px] text-white">{r.name}</div>
                  <div className="text-[12px] font-mono text-zinc-400">{r.erl || "—"}</div>
                  <div className={`text-[12px] font-mono ${r.elin ? "text-zinc-400" : "text-amber-400"}`}>{r.elin || "—"}</div>
                  <div className="text-[12px] text-zinc-400 truncate">{r.location || "—"}</div>
                  <div className={`text-[12px] ${r.status === "compliant" ? "text-emerald-400" : ["non-compliant", "review"].includes(r.status) ? "text-red-400" : "text-zinc-300"}`}>{E911_LABEL[r.status] || r.status}</div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {rooms.length > 0 && (
          <Section title="Room Collaboration Endpoints" icon={MonitorPlay}>
            <div className="rounded-md border border-zinc-800 overflow-hidden">
              <div className="grid grid-cols-[1.4fr_1fr_1.4fr_1.2fr_0.9fr] gap-2 px-3 py-2 bg-zinc-950 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                <span>Room</span><span>Device</span><span>Platform</span><span>Location</span><span>Status</span>
              </div>
              {rooms.map((r) => (
                <div key={r.id} className="grid grid-cols-[1.4fr_1fr_1.4fr_1.2fr_0.9fr] gap-2 px-3 py-2 border-b border-zinc-900 last:border-0 items-center">
                  <div className="text-[13px] text-white">{r.name}</div>
                  <div className="text-[12px] text-zinc-400">{r.model}</div>
                  <div className="text-[12px] text-zinc-400">{r.platform}</div>
                  <div className="text-[12px] text-zinc-400 truncate">{r.location || "—"}</div>
                  <div className={`text-[12px] ${r.status === "registered" ? "text-emerald-400" : ["offline", "issue"].includes(r.status) ? "text-red-400" : "text-zinc-300"}`}>{ROOM_LABEL[r.status] || r.status}</div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {drTests.length > 0 && (
          <Section title="DR / Failover Test Log" icon={LifeBuoy}>
            <div className="space-y-2">
              {drTests.map((t) => (
                <div key={t.id} className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] text-white font-semibold">{t.scenario}</span>
                    <span className={`text-[11px] font-semibold ${t.result === "pass" ? "text-emerald-400" : t.result === "fail" ? "text-red-400" : "text-amber-400"}`}>{DR_LABEL[t.result] || t.result}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1 text-[12px] text-zinc-400 font-mono">
                    {t.test_date && <span><span className="text-zinc-600">Date:</span> {t.test_date}</span>}
                    {t.rto_target && <span><span className="text-zinc-600">RTO target:</span> {t.rto_target}</span>}
                    {t.rto_actual && <span><span className="text-zinc-600">RTO actual:</span> {t.rto_actual}</span>}
                    {t.rpo_actual && <span><span className="text-zinc-600">RPO:</span> {t.rpo_actual}</span>}
                  </div>
                  {t.notes && <p className="text-[12px] text-zinc-500 mt-1">{t.notes}</p>}
                </div>
              ))}
            </div>
          </Section>
        )}

        <Section title={data.drs_backup.title} icon={DatabaseBackup}>
          <StepContent step={data.drs_backup} />
        </Section>

        <Section title={`Migration Phases (${data.phases.length})`}>
          {data.phases.map((p, i) => <StepCard key={p.id} step={p} idx={i + 1} />)}
        </Section>

        {data.jabber_migration && (
          <Section title={data.jabber_migration.title}>
            <p className="text-[13px] text-zinc-400 mb-3">{data.jabber_migration.summary}</p>
            {data.jabber_migration.steps.map((s, i) => <StepCard key={s.id} step={s} idx={i + 1} />)}
          </Section>
        )}

        {data.unity_migration && (
          <Section title={data.unity_migration.title}>
            <p className="text-[13px] text-zinc-400 mb-3">{data.unity_migration.summary}</p>
            {data.unity_migration.steps.map((s, i) => <StepCard key={s.id} step={s} idx={i + 1} />)}
          </Section>
        )}

        {data.imp_migration && (
          <Section title={data.imp_migration.title}>
            <p className="text-[13px] text-zinc-400 mb-3">{data.imp_migration.summary}</p>
            {data.imp_migration.steps.map((s, i) => <StepCard key={s.id} step={s} idx={i + 1} />)}
          </Section>
        )}

        {data.expressway_migration && (
          <Section title={data.expressway_migration.title}>
            <p className="text-[13px] text-zinc-400 mb-3">{data.expressway_migration.summary}</p>
            {data.expressway_migration.steps.map((s, i) => <StepCard key={s.id} step={s} idx={i + 1} />)}
          </Section>
        )}

        {data.contact_center_note && (
          <Section title={data.contact_center_note.title}>
            <p className="text-[13px] text-zinc-400 mb-2">{data.contact_center_note.summary}</p>
            <ul className="space-y-1.5">
              {data.contact_center_note.considerations.map((c, i) => (
                <li key={i} className="text-sm text-zinc-300">• {c}</li>
              ))}
            </ul>
          </Section>
        )}

        <Section title="PSTN Strategy" >
          {data.pstn_paths.map((path) => (
            <div key={path.id} className="mb-5">
              <div className="font-heading font-bold text-white">{path.name}{path.recommended ? " ★" : ""}</div>
              <div className="text-[12px] text-zinc-400 mb-2"><span className="text-zinc-600">When:</span> {path.when_to_use}</div>
              {path.steps.map((s, i) => <StepCard key={s.id} step={s} idx={i + 1} />)}
            </div>
          ))}
        </Section>

        {data.drs_restore && (
          <Section title={data.drs_restore.title} icon={RotateCcw}>
            <StepContent step={data.drs_restore} />
          </Section>
        )}

        <Section title="Firewall / Egress Requirements" icon={Shield}>
          <div className="space-y-2">
            {data.firewall.map((f, i) => (
              <div key={i} className="flex items-start gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-2.5">
                <span className="font-mono text-[12px] text-emerald-300 shrink-0 min-w-[140px]">{f.proto}</span>
                <span className="text-[13px] text-zinc-400">{f.purpose}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Prerequisites / Next Steps">
          <ul className="space-y-1.5">
            {data.prerequisites.map((p, i) => <li key={i} className="text-sm text-zinc-300">• {p}</li>)}
          </ul>
        </Section>

        <Section title="Handoff Package">
          <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 mb-3">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">Kickoff Script</div>
            <p className="text-sm text-zinc-300 italic">"{data.handoff.kickoff_script}"</p>
          </div>
          <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Email — {data.handoff.email_subject}</div>
            <pre className="font-sans text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed mt-2">{data.handoff.email_body}</pre>
          </div>
        </Section>

        <div className="mt-8 pt-4 border-t border-zinc-800 text-[11px] text-zinc-600 font-mono text-center">
          UC Engineer Pro · CUCM → Webex Cloud Migration Playbook
        </div>
      </div>
    </div>
  );
}
